import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, Box, TextFieldProps, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {CancelToken} from "axios";

interface CustomerOption {
    id: string;
    name: string;
    priceMatrixCount: number;
}

interface CustomerAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<CustomerOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: CustomerOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: CustomerOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * CustomerAutocomplete Component
 *
 * A searchable dropdown component for selecting customers with the following features:
 * - Asynchronous search with debouncing
 * - Custom option rendering with name and city display
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * @component
 */
const PriceMatrixExceptionalCustomersAutocomplete = <M extends boolean = false, >({
                                                                                      textFieldProps = {},
                                                                                      form = false,
                                                                                      name = undefined,
                                                                                      required = false,
                                                                                      setLoadingOptions: setLoadingOptionsProps,
                                                                                      renderOption: customRenderOption,
                                                                                      ...props
                                                                                  }: CustomerAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches customer options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching customer options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.getPriceMatrixExceptionalCustomers({
                pagination: {pageSize: 20, currentPage: 1},
                keyword: keywords,
                sorts: [],
            }, cancelToken);
            if (!isMounted())
                return [];
            return response?.data?.items ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two customer options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: CustomerOption, value: CustomerOption | null) => option.id === value?.id, []);

    /**
     * Extracts the display label from a customer option
     */
    const getOptionLabel = useCallback((option: CustomerOption) => option?.name ?? "", []);

    /**
     * Gets the unique key for a customer option
     */
    const getOptionKey = useCallback((option: CustomerOption) => option?.id ?? "", []);

    /**
     * Default render function for customer options
     * Displays a two-column layout with name and city
     *
     * @param props - HTML attributes for the list item
     * @param option - Customer option to render
     */
    const defaultRenderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
        option: CustomerOption
    ) => {
        const {key: _, ...otherProps} = props;
        return (
            <Box component="li" {...otherProps} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                {/* Header row with column labels */}
                <Grid2 container alignItems={'center'} width={'100%'} spacing={1}>
                    <Grid2 xs={7}>
                        <Typography variant="caption" color="text.secondary">
                            Name
                        </Typography>
                    </Grid2>
                    <Grid2 xs={5}>
                        <Typography variant="caption" color="text.secondary">
                            Price Matrices
                        </Typography>
                    </Grid2>
                </Grid2>
                {/* Data row with customer information */}
                <Grid2 container alignItems={'center'} width={'100%'} spacing={1}>
                    <Grid2 xs={7}>
                        <Typography variant="body2" color="secondary.main">
                            {option.name ?? ""}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={5}>
                        <Typography variant="body2" color="secondary.main">
                            {option.priceMatrixCount ?? ""}
                        </Typography>
                    </Grid2>
                </Grid2>
            </Box>
        );
    }, []);

    /**
     * Render function that combines custom and default option rendering
     * Allows for customization while maintaining default layout as fallback
     */
    const renderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement>,
        option: CustomerOption,
        state: AutocompleteRenderOptionState
    ) => {
        if (customRenderOption) {
            return customRenderOption(props, option, state, defaultRenderOption);
        }
        return defaultRenderOption(props, option);
    }, [customRenderOption, defaultRenderOption]);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<CustomerOption, M>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
            renderOption={renderOption}
        />
    );
};

export default PriceMatrixExceptionalCustomersAutocomplete;